import * as THREE from 'three';
import { PALETTE, TIME_OF_DAY_SKY, SEASON_PALETTE } from '../palette';
import { GRID_SIZE, CELL_SIZE, shapeOutline, TWIN_GEOM, TWIN_HEIGHT } from '../sim/garden';
import type { IslandShape } from '../sim/garden';
import type { TimeOfDay } from '../sim/daynight';
import type { Season } from '../sim/season';

// CELL_SIZE は sim（garden.ts）が正本。描画もそれを共有するため再エクスポートする
// （既存の `import { CELL_SIZE } from './diorama'` を壊さないため）。
export { CELL_SIZE };
/** 庭面のワールド Y 座標（グリッド面の高さ）。 */
export const GARDEN_Y = 0.72;
/** グリッド全体のワールド幅。 */
export const GRID_SPAN = GRID_SIZE * CELL_SIZE;

/**
 * カフェのドア前の島ローカル XZ 座標（行列の先頭が立つ場所）。
 *
 * カフェは island ローカルで (-1.6, _, -1.6) に置かれ、ドアは正面（+Z 面）の
 * cafe ローカル (0.55, _, half+α)（half=W/2=1.1）にある。両者を足した XZ が
 * ドア前。行列はここから手前（+X+Z 方向＝カメラ側）へ伸ばす。客ビューが
 * 「同時滞在上限に達したら並ぶ」位置の基準に使う。
 */
export const CAFE_DOOR_LOCAL = { x: -1.6 + 0.55, z: -1.6 + 1.15 } as const;

/**
 * グリッド座標 (gx, gy)（0..GRID_SIZE-1）をセル中心のワールド XZ に変換する。
 * グリッドは原点中心。庭ビュー・プレビュー・ピッキングで共有する。
 */
export function gridToWorld(gx: number, gy: number): { x: number; z: number } {
  const half = GRID_SPAN / 2;
  return {
    x: (gx + 0.5) * CELL_SIZE - half,
    z: (gy + 0.5) * CELL_SIZE - half,
  };
}

/**
 * ローカル（island 空間）XZ をグリッド座標へ逆変換する。範囲外は null。
 * ピッキング結果（ワールド点を island に変換した後）をセルに落とすのに使う。
 */
export function worldToGrid(localX: number, localZ: number): { gx: number; gy: number } | null {
  const half = GRID_SPAN / 2;
  const gx = Math.floor((localX + half) / CELL_SIZE);
  const gy = Math.floor((localZ + half) / CELL_SIZE);
  if (gx < 0 || gx >= GRID_SIZE || gy < 0 || gy >= GRID_SIZE) return null;
  return { gx, gy };
}

/**
 * 疑似3Dディオラマのシーン構築。
 *
 * - 小惑星（上面が平らな塊）＋ カフェ（箱）＋ 8×8 グリッド面
 * - isometric 風の OrthographicCamera で俯瞰
 * - パステルの宇宙背景（ラベンダー/ミント/ピンクの流線グラデ）
 * - ゆっくり漂う星パーティクル
 *
 * 実際のドット絵ルックは pixelate パイプライン（PixelPipeline）が、
 * このシーンを低解像度 RT に描いて NearestFilter 拡大することで生む。
 */
export interface Diorama {
  scene: THREE.Scene;
  camera: THREE.OrthographicCamera;
  /**
   * 小惑星・庭・カフェを乗せた回転ノード。庭ビュー（デコ）もここに add すると
   * ディオラマと一緒に自転する。座標は island ローカル空間。
   */
  island: THREE.Group;
  /**
   * グリッド面に一致する不可視のピッキング用平面（island の子）。
   * raycaster の対象にして「どのセルがタップされたか」を判定する。
   */
  pickPlane: THREE.Mesh;
  /**
   * カフェ本体の Group（island の子）。タップでメニューを開くため、main から
   * raycast の対象にする。中身（壁・屋根・窓）をまとめた当たり判定の代表。
   */
  cafe: THREE.Group;
  /** 毎フレーム呼ぶ。tSec は累計秒。 */
  update(tSec: number): void;
  /** 表示サイズ変更時に呼ぶ（カメラのアスペクト維持）。 */
  resize(width: number, height: number): void;
  /**
   * 自転の有効/無効。建設中は止めるとセルが狙いやすい（既定は有効）。
   * 止めても現在の角度は保持する（ガクつかない）。
   */
  setSpinning(on: boolean): void;
  /**
   * 島を手動で回す（v0.5.1）。deltaRad ぶん Y 回転を足し、自動自転を数秒ポーズする
   * （操作後の無操作でまた回り出す）。マウスホイール・ドラッグ・スワイプから呼ぶ。
   */
  nudgeRotation(deltaRad: number): void;
  /**
   * 「本日のスペシャル」の属性色でカフェの光を変える（v0.5.1）。hex=null で既定の暖色。
   * 丸窓の発光ブレンド・上空の光柱・弱い PointLight をまとめて切り替える。
   */
  setSpecialColor(hex: string | null): void;
  /**
   * 時間帯（v0.6.0）。空のグラデ・環境光・方向光・星の濃さ・カフェの発光ブーストを
   * 帯に合わせて切り替える（夜はランタン・窓・キノコの発光が映える）。
   */
  setTimeOfDay(tod: TimeOfDay): void;
  /**
   * 季節（v0.6.0）。小惑星上面（地面）の色・背景ティント・木の葉の寄り色を季節に
   * 合わせて切り替える（春=桜ピンク / 夏=濃いミント / 秋=オレンジ / 冬=雪白）。
   */
  setSeason(season: Season): void;
  /**
   * カフェ本体の表示/非表示（v0.7.1）。拡張島（カフェの無い「眺める庭」の島）を
   * 表示しているときは false にしてカフェを隠す。ホーム島では true。
   */
  setCafeVisible(on: boolean): void;
  /**
   * カルーセルの引き/寄り（v0.7.2）。wide=true で引き（隣島の見切れが見える広い画角）、
   * false で寄り（ホーム島の導線クローズアップ）。
   */
  setWideView(wide: boolean): void;
  /**
   * 表示中の島の形状を設定する（v0.7.3）。小惑星の上面メッシュを形状に合わせて作り直す
   * （有効セル判定 isCellInShape と同じ輪郭）。round（ホーム島）→ 従来の円柱。島切替時に呼ぶ。
   */
  setShape(shape: IslandShape): void;
  /**
   * カメラズーム倍率を設定する（v0.7.4、ピンチズーム）。1.0 = 等倍（基準 frustum）、>1 で寄る
   * （frustum を縮める＝拡大）、<1 で引く。下限・上限でクランプした実効値を返す。OrthographicCamera
   * の frustum を `基準frustum / zoom` にして拡縮する（ピンチ距離比に応じて main が呼ぶ）。
   */
  setZoom(zoom: number): number;
  /** 現在のズーム倍率を返す（v0.7.4）。 */
  getZoom(): number;
}

function col(hex: string): THREE.Color {
  return new THREE.Color(hex);
}

/**
 * 縦 3 色グラデの宇宙背景テクスチャを生成する。
 *
 * v0.6.0: 時間帯（昼夜）の 3 色 + 季節ティントを受けて空の色を変える。星雲の流線は
 * 夜ほど淡く（暗い空に白線が浮きすぎないように）描く。
 *
 * @param grad     上・中・下の 3 色（時間帯の TimeOfDaySky.grad）。
 * @param tint     季節ティント色（背景へ重ねる）。
 * @param tintA    ティントの強さ（0..1）。
 */
function makeSpaceBackground(
  grad: [string, string, string] = [PALETTE.spaceLavender, PALETTE.spaceMint, PALETTE.spacePink],
  tint?: string,
  tintA = 0,
): THREE.Texture {
  const w = 64;
  const h = 64;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;

  const g = ctx.createLinearGradient(0, 0, 0, h);
  g.addColorStop(0.0, grad[0]);
  g.addColorStop(0.5, grad[1]);
  g.addColorStop(1.0, grad[2]);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);

  // 季節ティントを淡く重ねる（背景全体を季節色へ少し寄せる）。
  if (tint && tintA > 0) {
    ctx.globalAlpha = tintA * 0.5; // 背景は控えめに（地面より弱く）
    ctx.fillStyle = tint;
    ctx.fillRect(0, 0, w, h);
    ctx.globalAlpha = 1;
  }

  // ゆるい流線（星雲）を斜めに重ねて宇宙っぽさを出す。
  ctx.globalAlpha = 0.16;
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 2;
  for (let i = 0; i < 5; i++) {
    const y = (i + 0.5) * (h / 5);
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.bezierCurveTo(w * 0.3, y - 8, w * 0.7, y + 8, w, y - 4);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.minFilter = THREE.NearestFilter;
  tex.magFilter = THREE.NearestFilter;
  return tex;
}

/**
 * 上面が平らな小惑星。低ポリ塊を頂点ノイズで軽く崩す。topMat を返して季節ティントに使う。
 *
 * v0.7.3: 島の形状（shape）で上面の輪郭を変える。round は従来どおり円柱（互換維持）。
 * 非 round は shapeOutline（有効セル述語と同閾値）を 1.08 倍に広げた輪郭から上面を作り、
 * 見た目とマス（isCellInShape）を一致させる（マスが浮く/欠ける不整合を作らない）。
 */
function makeAsteroid(shape: IslandShape = 'round'): { group: THREE.Group; topMat: THREE.MeshStandardMaterial } {
  const group = new THREE.Group();

  const topMat = new THREE.MeshStandardMaterial({
    color: col(PALETTE.asteroidTop),
    roughness: 0.95,
    metalness: 0.0,
    flatShading: true,
  });

  if (shape === 'round') {
    // 上面（庭がのる平らな円盤）。従来の見た目を完全維持。
    const topGeo = new THREE.CylinderGeometry(5.2, 4.4, 1.4, 10, 1);
    // 側面に軽い凹凸を入れて岩らしくする（決定的、Math.random は使わない）。
    const pos = topGeo.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < pos.count; i++) {
      const y = pos.getY(i);
      if (y < 0.6) {
        const x = pos.getX(i);
        const z = pos.getZ(i);
        const n = Math.sin(x * 2.1) * Math.cos(z * 1.7) * 0.18;
        pos.setX(i, x + n);
        pos.setZ(i, z + n);
      }
    }
    topGeo.computeVertexNormals();
    group.add(new THREE.Mesh(topGeo, topMat));

    // 下の岩塊（先細りで小惑星の底を演出）
    const baseGeo = new THREE.ConeGeometry(4.2, 4.5, 9, 1);
    const baseMat = new THREE.MeshStandardMaterial({
      color: col(PALETTE.asteroidSide), roughness: 1.0, metalness: 0.0, flatShading: true,
    });
    const base = new THREE.Mesh(baseGeo, baseMat);
    base.position.y = -2.9;
    base.rotation.y = Math.PI / 7;
    base.scale.y = -1; // 下向きの円錐
    group.add(base);
    return { group, topMat };
  }

  // --- twin（双子島）: 2 つの小惑星ディスク＋間の橋メッシュ（v0.7.4） ---
  // 動径サンプリングの単一輪郭では「2 円の分離＋橋」を表現できないので、専用に組む。
  // 有効セル述語（cellInShapeXZ の twin）と同じ TWIN_GEOM を使い、見た目とマスを一致させる。
  if (shape === 'twin') {
    const VIST = 1.08;
    const cx = TWIN_GEOM.centerX;
    const r = TWIN_GEOM.radius * VIST;
    // v0.7.6: 2 島に高低差。左ディスク +delta（高い）/ 右ディスク -delta（低い）を Y にオフセット。
    // ディスク本体と下の岩塊コーンを同じ dy だけ上下させる。garden_view のセル表示 Y（twinSurfaceY）も
    // 同じ delta で乗るので、ディスク上のデコ・枠が高さに正しく乗る（浮かない）。
    const delta = TWIN_HEIGHT.delta;
    // 左 = -cx → +delta、右 = +cx → -delta（twinSurfaceY と符号を一致させる）。
    for (const sx of [-cx, cx]) {
      const dy = sx < 0 ? delta : -delta;
      const disc = new THREE.Mesh(new THREE.CylinderGeometry(r, r * 0.84, 1.4, 16, 1), topMat);
      disc.position.set(sx, dy, 0);
      group.add(disc);
      // 各ディスクの下に小さな岩塊（先細り）で「浮いている塊」感。
      const coneMat = new THREE.MeshStandardMaterial({
        color: col(PALETTE.asteroidSide), roughness: 1.0, metalness: 0.0, flatShading: true,
      });
      const cone = new THREE.Mesh(new THREE.ConeGeometry(r * 0.86, 3.6, 10, 1), coneMat);
      cone.position.set(sx, -2.4 + dy, 0);
      cone.scale.y = -1;
      group.add(cone);
    }

    // --- アーチ橋（v0.7.6）---
    // 床板を x 方向に ~10 セグメントに分け、各セグメントの Y を「両端の高さを結ぶ線形
    // ＋アーチの盛り上がり（sin 半波）」のカーブに乗せ、各セグメントをローカル傾き（z 軸まわり
    // 回転）で接続して滑らかなアーチにする。両端は左ディスク上面(高)・右ディスク上面(低)に合わせる。
    const deckTopY = 0.72; // 平らだった頃の床上面（ディスク上面の基準 Y）
    const halfW = TWIN_GEOM.bridgeHalfW * VIST;
    const wood = stdMat('#D8B89A', 0.9); // 床板（明るい木）
    const railWood = stdMat('#C49A72', 0.9); // 手すり（やや濃い木）

    // 橋の X 範囲: 2 島の「内側の縁同士」をつなぐ（v0.7.6.1）。中心まで伸ばすと島の真ん中
    // まで橋が浸食するので、各ディスクの内縁（中心 ∓cx ± 視覚半径 r = ∓(cx - r) ≈ ±0.84）に
    // わずかな乗りしろ overlap を足した位置を端にする。これで橋は隙間だけを渡し、縁に少し乗る。
    const innerEdge = cx - r; // 視覚ディスクの内側の縁の |x|（≈0.84）
    const overlap = 0.5; // 縁への乗りしろ（橋端が各ディスクの縁に少し乗る）
    const x0 = -(innerEdge + overlap); // 左端（高い島の縁）
    const x1 = innerEdge + overlap; // 右端（低い島の縁）
    const y0 = deckTopY + delta; // 左端の床上面 Y（高い）
    const y1 = deckTopY - delta; // 右端の床上面 Y（低い）
    const archH = 0.42; // アーチの盛り上がり（中央が両端の線形 + これだけ上がる）
    const SEG = 10; // セグメント数

    // パラメータ t(0..1) → 床上面のワールド (x, y)。線形（両端高さ補間）+ sin 半波の盛り上がり。
    const bx = (t: number): number => x0 + (x1 - x0) * t;
    const by = (t: number): number => y0 + (y1 - y0) * t + Math.sin(t * Math.PI) * archH;

    const deckThick = 0.14;
    // 床板セグメント: 各区間 [t_i, t_{i+1}] を 1 枚の Box で表し、中点に置いて区間の傾きで回す。
    for (let i = 0; i < SEG; i++) {
      const ta = i / SEG;
      const tb = (i + 1) / SEG;
      const xa = bx(ta), ya = by(ta);
      const xb = bx(tb), yb = by(tb);
      const mx = (xa + xb) / 2;
      const my = (ya + yb) / 2;
      const len = Math.hypot(xb - xa, yb - ya);
      const ang = Math.atan2(yb - ya, xb - xa); // z 軸まわりの傾き
      // 板の上面が my に来るよう、板中心を厚みの半分だけ下げる（傾きに沿って法線方向へ）。
      const plank = new THREE.Mesh(new THREE.BoxGeometry(len * 1.02, deckThick, halfW * 2), wood);
      plank.position.set(mx - Math.sin(ang) * (deckThick / 2), my - Math.cos(ang) * (deckThick / 2), 0);
      plank.rotation.z = ang;
      group.add(plank);
    }

    // 両側の手すり: 笠木（同じアーチカーブに沿うセグメント角材）＋一定間隔の柱（床から上に伸ばす）。
    const railH = 0.29; // 笠木の床上面からの高さ
    for (const sz of [-halfW, halfW]) {
      for (let i = 0; i < SEG; i++) {
        const ta = i / SEG;
        const tb = (i + 1) / SEG;
        const xa = bx(ta), ya = by(ta) + railH;
        const xb = bx(tb), yb = by(tb) + railH;
        const mx = (xa + xb) / 2, my = (ya + yb) / 2;
        const len = Math.hypot(xb - xa, yb - ya);
        const ang = Math.atan2(yb - ya, xb - xa);
        const rail = new THREE.Mesh(new THREE.BoxGeometry(len * 1.05, 0.05, 0.05), railWood);
        rail.position.set(mx, my, sz);
        rail.rotation.z = ang;
        group.add(rail);
      }
      // 柱: アーチ床の各位置から笠木まで縦に伸ばす。
      const posts = 5;
      for (let i = 0; i < posts; i++) {
        const t = (i + 0.5) / posts;
        const px = bx(t);
        const floorY = by(t);
        const post = new THREE.Mesh(new THREE.BoxGeometry(0.05, railH, 0.05), railWood);
        post.position.set(px, floorY + railH / 2, sz);
        group.add(post);
      }
    }
    return { group, topMat };
  }

  // --- 非 round 形状: 輪郭から上面（押し出し板）＋側壁＋底を作る ---
  // 輪郭は有効セル述語と同閾値。視覚は 1.08 倍に広げてセルが縁から落ちないようにする
  // （round の 5.2/4.8≒1.083 と同比）。
  const VIS = 1.08;
  // 96 分割で輪郭をなめらかに（clover の葉の凹凸を取りこぼさず、有効セルを包む）。
  const outline = shapeOutline(shape, 96).map((p) => new THREE.Vector2(p.x * VIS, p.z * VIS));
  const shape2d = new THREE.Shape(outline);

  // 上面の板（厚み 1.4、上面が平ら）。Extrude は XY 平面に作るので X 軸まわりに寝かせる。
  const topGeo = new THREE.ExtrudeGeometry(shape2d, { depth: 1.4, bevelEnabled: false, steps: 1 });
  topGeo.rotateX(Math.PI / 2); // XY → XZ（板を寝かせる）
  topGeo.translate(0, 0.7, 0); // 上面を y≈0.7 に、下面を y≈-0.7 に
  topGeo.computeVertexNormals();
  group.add(new THREE.Mesh(topGeo, topMat));

  // 側面＋底: 輪郭を下方へすぼめた角錐台（先細りで小惑星の底）。輪郭リングと下の一点で作る。
  const sideMat = new THREE.MeshStandardMaterial({
    color: col(PALETTE.asteroidSide), roughness: 1.0, metalness: 0.0, flatShading: true,
  });
  const apexY = -4.6; // 底の尖り
  const ringY = -0.7; // 上面板の下端
  const verts: number[] = [];
  const n = outline.length;
  for (let i = 0; i < n; i++) {
    const a = outline[i];
    const b = outline[(i + 1) % n];
    // 三角形（リング辺 a→b と底の尖点）。底はやや内側へ寄せて「塊」感を出す。
    verts.push(a.x * 0.96, ringY, a.y * 0.96);
    verts.push(b.x * 0.96, ringY, b.y * 0.96);
    verts.push(0, apexY, 0);
  }
  const sideGeo = new THREE.BufferGeometry();
  sideGeo.setAttribute('position', new THREE.Float32BufferAttribute(verts, 3));
  sideGeo.computeVertexNormals();
  group.add(new THREE.Mesh(sideGeo, sideMat));

  return { group, topMat };
}

/** 発光ベタ塗りマテリアル（ライティング非依存）。 */
function glowMat(hex: string, opacity = 1): THREE.MeshBasicMaterial {
  return new THREE.MeshBasicMaterial({
    color: col(hex),
    transparent: opacity < 1,
    opacity,
  });
}

/** 標準マテリアル（パステルを潰さない設定の薄ラッパ）。 */
function stdMat(hex: string, roughness = 0.85): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: col(hex),
    roughness,
    metalness: 0,
    flatShading: true,
  });
}

/** カフェの戻り値（毎フレーム湯気とライトを動かすため update を持つ）。 */
interface CafeBuild {
  group: THREE.Group;
  update(tSec: number): void;
  /**
   * 「本日のスペシャル」の光を設定する（v0.5.1）。hex=属性色を渡すと丸窓の発光が
   * その色にブレンドされ、上空への淡い光柱と弱い PointLight が灯る。null で既定（暖色）に戻す。
   */
  setSpecialColor(hex: string | null): void;
  /**
   * 発光ブースト（v0.6.0 昼夜）。夜ほど大きく（>1）して丸窓・側窓・ストリングライト・
   * ドアノブの「灯り」を際立たせる。昼は等倍〜やや控えめ。
   */
  setGlowBoost(boost: number): void;
}

/**
 * カフェ本体。コンセプトアート 2_cafe_b.png に寄せた立方体カフェ:
 *  - クリーム/ピーチの壁、ミントの庇・見切り、ラベンダーの屋上デッキ
 *  - 大きな丸窓（暖色で発光する主役）、淡い藤色のドア、右手のガラス窓
 *  - 屋上の双子煙突＋ゆっくり立ち上る湯気パフ
 *  - 軒先のストリングライト（小さな発光球の連なり、ゆらぎ点滅）
 *
 * 低ポリ＋ピクセレートパイプライン前提。テクスチャは使わず形と発光で見せる。
 * カフェは概ね 2.2 角の箱。正面（+Z 面）を丸窓・ドアの「顔」にする。
 */
function makeCafe(): CafeBuild {
  const cafe = new THREE.Group();
  const W = 2.2; // 本体の一辺
  const H = 1.7; // 壁の高さ
  const half = W / 2;

  // --- 本体（クリームの箱） ---
  const body = new THREE.Mesh(new THREE.BoxGeometry(W, H, W), stdMat(PALETTE.cafeCream, 0.8));
  body.position.y = H / 2;
  cafe.add(body);

  // 正面と側面にピーチの下見板を薄く重ねて二色にする（コンセプトの淡いピーチ壁）。
  for (const [px, pz, rotY] of [
    [0, half + 0.02, 0],
    [half + 0.02, 0, Math.PI / 2],
  ] as const) {
    const panel = new THREE.Mesh(new THREE.PlaneGeometry(W * 0.98, H * 0.98), stdMat(PALETTE.cafePeach, 0.85));
    panel.position.set(px, H / 2, pz);
    panel.rotation.y = rotY;
    cafe.add(panel);
  }

  // --- 屋上まわり（平らなデッキ＋ミントのパラペット） ---
  const deck = new THREE.Mesh(new THREE.BoxGeometry(W * 0.96, 0.12, W * 0.96), stdMat(PALETTE.cafeRoofDeck, 0.9));
  deck.position.y = H + 0.02;
  cafe.add(deck);
  // パラペット（屋根の縁のミントの帯）を四辺に回す
  const parapetMat = stdMat(PALETTE.cafeTrim, 0.7);
  for (const [sx, sz, sw, sd] of [
    [0, half, W + 0.12, 0.12],
    [0, -half, W + 0.12, 0.12],
    [half, 0, 0.12, W + 0.12],
    [-half, 0, 0.12, W + 0.12],
  ] as const) {
    const bar = new THREE.Mesh(new THREE.BoxGeometry(sw, 0.22, sd), parapetMat);
    bar.position.set(sx, H + 0.13, sz);
    cafe.add(bar);
  }

  // --- 軒（壁の上端を一周するミントの庇） ---
  const eaveMat = stdMat(PALETTE.cafeTrim, 0.7);
  const eave = new THREE.Mesh(new THREE.BoxGeometry(W + 0.18, 0.12, W + 0.18), eaveMat);
  eave.position.y = H - 0.06;
  cafe.add(eave);

  // --- 大きな丸窓（正面・暖色発光＝主役） ---
  const winGroup = new THREE.Group();
  winGroup.position.set(-0.45, H * 0.52, half + 0.04);
  // ミントの窓枠（リング）
  const frame = new THREE.Mesh(new THREE.TorusGeometry(0.46, 0.07, 8, 20), stdMat(PALETTE.cafeTrim, 0.6));
  winGroup.add(frame);
  // 発光ガラス（外側の暖色 → 中心の明るい芯の二枚重ねで「灯り」を出す）
  const glass = new THREE.Mesh(new THREE.CircleGeometry(0.43, 24), glowMat(PALETTE.cafeWindowGlow));
  glass.position.z = 0.005;
  winGroup.add(glass);
  const core = new THREE.Mesh(new THREE.CircleGeometry(0.26, 20), glowMat(PALETTE.cafeWindowGlowCore));
  core.position.z = 0.01;
  winGroup.add(core);
  // 窓の十字桟（コンセプトの丸窓の意匠）
  const muntinMat = stdMat(PALETTE.cafeTrim, 0.6);
  const mh = new THREE.Mesh(new THREE.BoxGeometry(0.84, 0.05, 0.02), muntinMat);
  mh.position.z = 0.02;
  winGroup.add(mh);
  const mv = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.84, 0.02), muntinMat);
  mv.position.z = 0.02;
  winGroup.add(mv);
  cafe.add(winGroup);

  // --- ドア（正面右手、淡い藤色） ---
  const door = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.95, 0.06), stdMat(PALETTE.cafeDoor, 0.8));
  door.position.set(0.55, 0.48, half + 0.03);
  cafe.add(door);
  const doorFrame = new THREE.Mesh(new THREE.BoxGeometry(0.6, 1.05, 0.04), stdMat(PALETTE.cafeTrim, 0.6));
  doorFrame.position.set(0.55, 0.53, half + 0.015);
  cafe.add(doorFrame);
  // ドアノブ
  const knob = new THREE.Mesh(new THREE.SphereGeometry(0.035, 8, 6), glowMat(PALETTE.warmOrange));
  knob.position.set(0.72, 0.48, half + 0.07);
  cafe.add(knob);

  // --- 側面（+X）の四角いガラス窓（店内がのぞく感じ。暖色で淡く灯す） ---
  const sideGlass = new THREE.Mesh(new THREE.PlaneGeometry(0.7, 0.55), glowMat(PALETTE.cafeWindowGlow, 0.78));
  sideGlass.position.set(half + 0.05, H * 0.55, 0.15);
  sideGlass.rotation.y = Math.PI / 2;
  cafe.add(sideGlass);
  const sideFrame = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.66, 0.82), stdMat(PALETTE.cafeTrim, 0.6));
  sideFrame.position.set(half + 0.04, H * 0.55, 0.15);
  cafe.add(sideFrame);

  // --- 屋上の双子煙突 ---
  const chimneyMat = stdMat(PALETTE.cafeChimney, 0.85);
  const chimneyTops: THREE.Vector3[] = [];
  for (const cx of [0.18, 0.52]) {
    const ch = new THREE.Mesh(new THREE.BoxGeometry(0.26, 0.5, 0.26), chimneyMat);
    ch.position.set(cx, H + 0.35, -0.45);
    cafe.add(ch);
    const cap = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.08, 0.32), stdMat(PALETTE.cafeTrim, 0.7));
    cap.position.set(cx, H + 0.62, -0.45);
    cafe.add(cap);
    chimneyTops.push(new THREE.Vector3(cx, H + 0.66, -0.45));
  }

  // --- 湯気パフ（小さな球が煙突から昇って消える） ---
  // プールで作り、update でループ上昇させる（GC を避ける）。
  const PUFFS_PER_CHIMNEY = 4;
  const PUFF_RISE = 1.3; // 昇る高さ
  const PUFF_PERIOD = 3.2; // 1 個が出て消えるまでの秒数
  interface Puff {
    mesh: THREE.Mesh;
    base: THREE.Vector3;
    offset: number; // 位相（0..1）
    sway: number;
  }
  const puffs: Puff[] = [];
  const puffMat = glowMat(PALETTE.steam, 0.85);
  chimneyTops.forEach((top, ci) => {
    for (let i = 0; i < PUFFS_PER_CHIMNEY; i++) {
      const mesh = new THREE.Mesh(new THREE.IcosahedronGeometry(0.1, 0), puffMat.clone());
      cafe.add(mesh);
      puffs.push({
        mesh,
        base: top.clone(),
        offset: (i / PUFFS_PER_CHIMNEY + ci * 0.5) % 1,
        sway: ci === 0 ? 1 : -1,
      });
    }
  });

  // --- 軒先のストリングライト（正面・小さな発光球の連なり） ---
  const lightBulbs: THREE.Mesh[] = [];
  const N = 7;
  for (let i = 0; i < N; i++) {
    const t = i / (N - 1);
    const bx = THREE.MathUtils.lerp(-half + 0.1, half - 0.1, t);
    // 軒下にたるみ（弧）を持たせる
    const sag = Math.sin(t * Math.PI) * 0.14;
    const bulb = new THREE.Mesh(new THREE.SphereGeometry(0.05, 8, 6), glowMat(PALETTE.stringLight).clone());
    bulb.position.set(bx, H - 0.16 - sag, half + 0.06);
    cafe.add(bulb);
    lightBulbs.push(bulb);
  }

  // --- 本日のスペシャルの光（v0.5.1）---
  // 既定は暖色。スペシャルが設定されるとこの色を属性色にブレンドして「灯り」を変える。
  const baseGlow = col(PALETTE.cafeWindowGlow);
  const baseCore = col(PALETTE.cafeWindowGlowCore);
  let specialColor: THREE.Color | null = null;

  // カフェ上空への淡い光柱（半透明シリンダー）。スペシャル時だけ表示。
  const pillar = new THREE.Mesh(
    new THREE.CylinderGeometry(0.55, 0.95, 4.2, 16, 1, true),
    new THREE.MeshBasicMaterial({
      color: baseGlow.clone(),
      transparent: true,
      opacity: 0,
      side: THREE.DoubleSide,
      depthWrite: false,
    }),
  );
  pillar.position.set(0, H + 2.0, 0);
  pillar.visible = false;
  cafe.add(pillar);

  // 弱い PointLight（庭をほんのり属性色に染める）。スペシャル時だけ強める。
  const specialLight = new THREE.PointLight(baseGlow.clone(), 0, 4.5, 2);
  specialLight.position.set(-0.45, H * 0.6, 0.6);
  cafe.add(specialLight);

  // --- 発光ブースト（v0.6.0 昼夜）---
  // 丸窓・側窓・芯の発光色のベースを覚えておき、ブースト倍率で明るさを上げ下げする。
  // ストリングライト・ドアノブも同様に倍率を update で掛ける（update が明滅と合成）。
  let glowBoost = 1.0;
  const baseGlassCol = baseGlow.clone();
  const baseCoreCol = baseCore.clone();

  function applyWindowGlow(): void {
    // スペシャル色があればブレンド、無ければ素の暖色。さらにブーストを掛ける。
    const glowCol = specialColor ? baseGlassCol.clone().lerp(specialColor, 0.55) : baseGlassCol.clone();
    const coreCol = specialColor ? baseCoreCol.clone().lerp(specialColor, 0.4) : baseCoreCol.clone();
    glowCol.multiplyScalar(glowBoost);
    coreCol.multiplyScalar(Math.min(1.0, glowBoost)); // 芯は白飛びさせない（上げすぎない）
    (glass.material as THREE.MeshBasicMaterial).color.copy(glowCol);
    (core.material as THREE.MeshBasicMaterial).color.copy(coreCol);
    (sideGlass.material as THREE.MeshBasicMaterial).color.copy(glowCol);
  }

  function setSpecialColor(hex: string | null): void {
    specialColor = hex ? new THREE.Color(hex) : null;
    applyWindowGlow();
    // 光柱・PointLight の色も合わせる（表示/強度は update でアニメさせる）。
    (pillar.material as THREE.MeshBasicMaterial).color.copy(specialColor ?? baseGlow);
    specialLight.color.copy(specialColor ?? baseGlow);
    pillar.visible = specialColor != null;
  }

  function setGlowBoost(boost: number): void {
    glowBoost = boost;
    applyWindowGlow();
  }

  function update(tSec: number): void {
    // スペシャルの光柱・PointLight をゆっくり脈動させる（点いているときだけ）。
    if (specialColor) {
      const pulse = 0.5 + Math.sin(tSec * 1.5) * 0.25; // 0.25..0.75
      (pillar.material as THREE.MeshBasicMaterial).opacity = 0.14 * (pulse + 0.4);
      specialLight.intensity = 0.7 + pulse * 0.6;
      pillar.rotation.y = tSec * 0.15;
    } else {
      specialLight.intensity = 0;
    }

    // 湯気: 各パフは offset を起点にループ上昇しながらフェード＆膨張。
    for (const p of puffs) {
      const phase = (tSec / PUFF_PERIOD + p.offset) % 1;
      const rise = phase * PUFF_RISE;
      p.mesh.position.set(
        p.base.x + Math.sin(phase * 6.28 + p.offset * 6.28) * 0.12 * p.sway,
        p.base.y + rise,
        p.base.z,
      );
      const grow = 0.7 + phase * 1.1;
      p.mesh.scale.setScalar(grow);
      // 出はじめにふわっと現れ、上で消える（山なりの不透明度）。
      (p.mesh.material as THREE.MeshBasicMaterial).opacity = Math.sin(phase * Math.PI) * 0.85;
    }
    // ストリングライト: 個体ごとにゆっくり明滅させて生きた灯りに。夜は glowBoost で強める。
    for (let i = 0; i < lightBulbs.length; i++) {
      const m = lightBulbs[i].material as THREE.MeshBasicMaterial;
      m.transparent = true;
      const base = 0.75 + Math.sin(tSec * 2 + i * 1.3) * 0.25;
      m.opacity = Math.min(1, base * Math.max(1, glowBoost));
    }
  }

  return { group: cafe, update, setSpecialColor, setGlowBoost };
}

// グリッド線は garden_view が「空き＆有効セルだけ」をセル単位の枠で描く
// （v0.5.1 ユーザーレビュー: デコのあるセルの枠は出さない／島の円なりに端を欠かす）。
// diorama 側の一枚もの GridHelper は廃止した。

/**
 * グリッド面に重ねる不可視のピッキング平面。raycaster の交点から
 * セルを割り出すために使う（見た目には出さない）。
 */
function makePickPlane(): THREE.Mesh {
  const geo = new THREE.PlaneGeometry(GRID_SPAN, GRID_SPAN);
  const mat = new THREE.MeshBasicMaterial({ visible: false });
  const plane = new THREE.Mesh(geo, mat);
  plane.rotation.x = -Math.PI / 2; // XZ 平面に寝かせる
  plane.position.y = GARDEN_Y;
  plane.name = 'pickPlane';
  return plane;
}

/** ゆっくり漂う星パーティクル。 */
function makeStars(): { points: THREE.Points; basePositions: Float32Array } {
  const count = 140;
  const positions = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    // 決定的に散らす（疑似乱数: 黄金角ベース）
    const a = i * 2.399963;
    const r = 8 + (i % 7);
    positions[i * 3 + 0] = Math.cos(a) * r;
    positions[i * 3 + 1] = ((i * 1.37) % 14) - 5;
    positions[i * 3 + 2] = Math.sin(a) * r - 4;
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  const mat = new THREE.PointsMaterial({
    color: col(PALETTE.cafeCream),
    size: 0.16,
    sizeAttenuation: true,
    transparent: true,
    opacity: 0.9,
  });
  const points = new THREE.Points(geo, mat);
  return { points, basePositions: positions.slice() };
}

export function createDiorama(): Diorama {
  const scene = new THREE.Scene();

  // 昼夜・季節の現在値（setTimeOfDay / setSeason で更新。背景の再生成に使う）。
  let curTod: TimeOfDay = 'day';
  let curSeason: Season = 'spring';

  scene.background = makeSpaceBackground();

  // isometric 風の正射影カメラ。frustum は表示モードで可変（v0.7.2）:
  //  - ホーム島（導線を見せる）: 少し寄せて（小さい frustum）客・歩道・入口を読みやすく。
  //  - カルーセル（隣島が見切れる）: 引き（大きい frustum）で周囲の島も視界に入れる。
  const FRUSTUM_HOME = 6.0; // 寄り（導線クローズアップ。v0.7.8 で 6.6→6.0 と少し寄せて島を大きく）
  const FRUSTUM_WIDE = 7.6; // 引き（カルーセルで隣島の見切れを見せる。v0.7.8 で 8.4→7.6）
  let frustum = FRUSTUM_HOME;
  let lastAspect = 1;
  // ピンチズーム倍率（v0.7.4）。>1 で寄る（frustum を縮める）、<1 で引く。下限・上限でクランプ。
  const ZOOM_MIN = 0.7; // 引きの下限（広く見渡せる）
  const ZOOM_MAX = 2.2; // 寄りの上限（席・客に寄れる）
  let zoom = 1.0;
  const camera = new THREE.OrthographicCamera(-frustum, frustum, frustum, -frustum, 0.1, 100);
  camera.position.set(14, 13, 14);
  // 入口(島手前＝+X+Z 側)が画角に入るよう注視点を少し手前へ寄せる。
  camera.lookAt(0.6, 1.4, 0.6);

  // ライティング: 全体を持ち上げる環境光 + 方向光（パステルを潰さない）
  const ambient = new THREE.AmbientLight(0xffffff, 0.85);
  scene.add(ambient);
  const key = new THREE.DirectionalLight(0xffffff, 0.9);
  key.position.set(6, 12, 8);
  scene.add(key);
  const fill = new THREE.DirectionalLight(col(PALETTE.crystalBlue), 0.35);
  fill.position.set(-8, 4, -6);
  scene.add(fill);

  // 小惑星・庭・カフェは一体（ディオラマ）として一緒に自転させたいので、
  // 一つの回転ノードにまとめて乗せる。
  const island = new THREE.Group();
  scene.add(island);

  let curShape: IslandShape = 'round';
  let asteroid = makeAsteroid(curShape);
  island.add(asteroid.group);
  // 季節ティントの土台にする地面のベース色（季節色をこれに少し寄せる）。
  const baseGroundCol = col(PALETTE.asteroidTop);

  // グリッド線（セル枠）は garden_view が空き＆有効セルだけ描く（diorama では描かない）。

  const pickPlane = makePickPlane();
  island.add(pickPlane);

  const cafe = makeCafe();
  // カフェは庭の一角（奥手）に置く。丸窓のある +Z 面・側面窓の +X 面がともに
  // 俯瞰カメラ（+X+Z 側）を向くので、回さずに「顔」が見える。
  cafe.group.position.set(-1.6, 0.72, -1.6);
  island.add(cafe.group);

  // 星は宇宙背景側なので island には乗せない（一緒に回らない）。
  const stars = makeStars();
  scene.add(stars.points);

  // 自転は「経過時間 × 速度」を積算する方式にして、止めても角度が飛ばないようにする。
  const SPIN_RATE = 0.05; // rad/sec
  let spinning = true; // 建設モード等での明示的な停止フラグ
  let spinAngle = 0;
  let lastSpinT = 0;
  // 手動操作（ホイール/ドラッグ/スワイプ）でのポーズ。最後の手動操作からの猶予秒。
  const MANUAL_RESUME_SEC = 3.0;
  let manualPauseUntil = -1; // この累計秒まで自動自転をポーズ

  function setSpinning(on: boolean): void {
    spinning = on;
  }

  function nudgeRotation(deltaRad: number): void {
    spinAngle += deltaRad;
    // 手動操作したら数秒は自動自転を止める（操作の邪魔をしない）。
    manualPauseUntil = lastSpinT + MANUAL_RESUME_SEC;
  }

  function update(tSec: number): void {
    // ディオラマ全体をごくゆっくり自転させる（建設中・手動操作直後は停止）
    const dt = Math.max(0, tSec - lastSpinT);
    lastSpinT = tSec;
    const manuallyPaused = tSec < manualPauseUntil;
    if (spinning && !manuallyPaused) spinAngle += dt * SPIN_RATE;
    island.rotation.y = spinAngle;

    // カフェの湯気・ストリングライトを動かす
    cafe.update(tSec);

    // 星をゆっくり漂わせる
    const pos = stars.points.geometry.attributes.position as THREE.BufferAttribute;
    const base = stars.basePositions;
    for (let i = 0; i < pos.count; i++) {
      const bx = base[i * 3 + 0];
      const by = base[i * 3 + 1];
      const bz = base[i * 3 + 2];
      pos.setX(i, bx + Math.sin(tSec * 0.3 + i) * 0.25);
      pos.setY(i, by + Math.cos(tSec * 0.2 + i * 0.5) * 0.2);
      pos.setZ(i, bz);
    }
    pos.needsUpdate = true;
  }

  /** 背景テクスチャを今の時間帯×季節で作り直す（古いテクスチャは破棄）。 */
  function rebuildBackground(): void {
    const sky = TIME_OF_DAY_SKY[curTod] ?? TIME_OF_DAY_SKY.day;
    const sp = SEASON_PALETTE[curSeason] ?? SEASON_PALETTE.spring;
    const old = scene.background as THREE.Texture | null;
    scene.background = makeSpaceBackground(sky.grad, sp.tint, sp.tintStrength);
    if (old && (old as THREE.Texture).dispose) old.dispose();
  }

  function setTimeOfDay(tod: TimeOfDay): void {
    curTod = tod;
    const sky = TIME_OF_DAY_SKY[tod] ?? TIME_OF_DAY_SKY.day;
    // 環境光・方向光・星・カフェ発光を帯に合わせる。
    ambient.color.copy(col(sky.ambient));
    ambient.intensity = sky.ambientI;
    key.intensity = sky.keyI;
    (stars.points.material as THREE.PointsMaterial).opacity = sky.starOpacity;
    cafe.setGlowBoost(sky.glowBoost);
    rebuildBackground();
  }

  function setSeason(season: Season): void {
    curSeason = season;
    const sp = SEASON_PALETTE[season] ?? SEASON_PALETTE.spring;
    // 地面（小惑星上面）をベース色から季節色へ少し寄せる（潰さない程度に）。
    asteroid.topMat.color.copy(baseGroundCol.clone().lerp(col(sp.ground), sp.tintStrength + 0.25));
    rebuildBackground();
  }

  /**
   * 現在の frustum・aspect・zoom からカメラの投影を作り直す（v0.7.4 で zoom を統合）。
   *
   * v0.7.8: `eff` を「短い辺の半分の視野」として扱い、島が縦横どちらの向きでも必ず収まる
   * ようにする。旧実装は eff を常に縦半分にしていたため、スマホ縦持ち（aspect<1）では横が
   * eff×aspect と狭くなり島が小さく見えていた。横長（aspect≥1）の見た目は従来と同じ。
   */
  function applyCamera(): void {
    const eff = frustum / zoom; // zoom>1 で frustum を縮める＝寄る
    let halfW: number;
    let halfH: number;
    if (lastAspect >= 1) {
      // 横長: 縦を eff に固定、横はアスペクトで広げる（従来どおり）。
      halfH = eff;
      halfW = eff * lastAspect;
    } else {
      // 縦長（スマホ縦持ち）: 横を eff に固定、縦を広げる。島が横幅いっぱいに収まる。
      halfW = eff;
      halfH = eff / lastAspect;
    }
    camera.left = -halfW;
    camera.right = halfW;
    camera.top = halfH;
    camera.bottom = -halfH;
    camera.updateProjectionMatrix();
  }

  function resize(width: number, height: number): void {
    lastAspect = width / height;
    applyCamera();
  }

  function setCafeVisible(on: boolean): void {
    cafe.group.visible = on;
  }

  /**
   * 表示中の島の形状で小惑星上面を作り直す（v0.7.3）。旧メッシュを dispose し、新形状の
   * asteroid に差し替えてから現在の季節ティントを再適用する（地面色が形状切替で戻らないように）。
   */
  function setShape(shape: IslandShape): void {
    if (shape === curShape) return;
    curShape = shape;
    island.remove(asteroid.group);
    asteroid.group.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.geometry) m.geometry.dispose();
      const mat = m.material as THREE.Material | THREE.Material[] | undefined;
      if (Array.isArray(mat)) mat.forEach((x) => x.dispose());
      else mat?.dispose();
    });
    asteroid = makeAsteroid(shape);
    island.add(asteroid.group);
    // 季節ティントを新しい topMat に再適用（setSeason と同じ式）。
    const sp = SEASON_PALETTE[curSeason] ?? SEASON_PALETTE.spring;
    asteroid.topMat.color.copy(baseGroundCol.clone().lerp(col(sp.ground), sp.tintStrength + 0.25));
  }

  /**
   * カルーセル表示の引き/寄りを切り替える（v0.7.2）。wide=true で引き（隣島の見切れが
   * 見える広い画角）、false で寄り（ホーム島の導線クローズアップ）。アスペクトは維持。
   */
  function setWideView(wide: boolean): void {
    frustum = wide ? FRUSTUM_WIDE : FRUSTUM_HOME;
    applyCamera();
  }

  /** ピンチズーム倍率を設定（v0.7.4）。下限・上限でクランプし、実効値を返す。 */
  function setZoom(z: number): number {
    zoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, z));
    applyCamera();
    return zoom;
  }
  function getZoom(): number {
    return zoom;
  }

  return {
    scene,
    camera,
    island,
    pickPlane,
    cafe: cafe.group,
    update,
    resize,
    setSpinning,
    nudgeRotation,
    setSpecialColor: cafe.setSpecialColor,
    setTimeOfDay,
    setSeason,
    setCafeVisible,
    setWideView,
    setShape,
    setZoom,
    getZoom,
  };
}
